// The card receipts behind a request. Drawn only when the server sent
// `receipts`, which it never does for the person who asked.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StatusPill from '../Receipts/StatusPill';
import { useToast } from '../components/Toast/ToastContext';
import { formatEventDate } from '../utils/date-utils';
import AnchoredPopover from './AnchoredPopover';
import ReceiptCandidates from './ReceiptCandidates';
import { IconButton, buttonClass } from '../components/ui/Button';
import { Tooltip } from '../components/ui/Tooltip';
import { Panel } from '../components/ui/Panel';
import { ListRow, ListStack } from '../components/ui/ListRow';
import EmptyState from '../components/ui/EmptyState';
import {
  errorMessage,
  formatCents,
  useRequisitionsApi,
  type LinkedReceipt,
  type RequisitionDetail,
} from './requisitionsApi';

interface Props {
  requisition: RequisitionDetail;
  receipts: LinkedReceipt[];
  onUpdated: (r: RequisitionDetail) => void;
}

const LINK_TRIGGER = `${buttonClass('quiet', 'sm')} -ml-2`;

const UnlinkGlyph = () => (
  <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-4 w-4">
    <path
      fill="currentColor"
      d="M4.22 4.22a.75.75 0 0 1 1.06 0L8 6.94l2.72-2.72a.75.75 0 1 1 1.06 1.06L9.06 8l2.72 2.72a.75.75 0 1 1-1.06 1.06L8 9.06l-2.72 2.72a.75.75 0 0 1-1.06-1.06L6.94 8 4.22 5.28a.75.75 0 0 1 0-1.06Z"
    />
  </svg>
);

const ReceiptsCard: React.FC<Props> = ({ requisition: req, receipts, onUpdated }) => {
  const reqApi = useRequisitionsApi();
  const toast = useToast();
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  // Approved requests take their receipt through Mark bought instead.
  const canLink = req.can.link_receipt && req.status !== 'approved';
  const missing = req.receipt_to_follow && (req.status === 'bought' || req.status === 'arrived');

  const link = async (): Promise<boolean> => {
    setBusy('link');
    let latest: RequisitionDetail | null = null;
    let done = 0;
    try {
      // One call per receipt: the endpoint takes one. Each answer is the whole
      // request, so the last one that landed is what goes on screen.
      for (const pid of picked) {
        latest = await reqApi.linkReceipt(req.id, pid);
        done += 1;
      }
      toast.success(done === 1 ? 'Linked the receipt.' : `Linked ${done} receipts.`);
      return true;
    } catch (err) {
      const lead = done ? `Linked ${done} of ${picked.length}. ` : '';
      toast.error(lead + errorMessage(err, 'Could not link that receipt. Try again.'));
      return false;
    } finally {
      if (latest) onUpdated(latest);
      setBusy(null);
    }
  };

  const unlink = async (r: LinkedReceipt) => {
    setBusy(`unlink-${r.pid}`);
    try {
      const updated = await reqApi.unlinkReceipt(req.id, r.pid);
      onUpdated(updated);
      toast.success(
        updated.receipt_to_follow ? 'Unlinked. The request is back under Receipt missing.' : 'Unlinked the receipt.'
      );
    } catch (err) {
      toast.error(errorMessage(err, 'Could not unlink that receipt. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Panel title="Receipts">
      {receipts.length > 0 ? (
        <ListStack
          items={receipts}
          getKey={(r) => r.pid}
          on="panel"
          spacing="loose"
          label="Linked receipts"
          renderRow={(r) => (
            <ListRow
              actions={
                req.can.link_receipt ? (
                  <Tooltip label="Unlink this receipt from the request">
                    <IconButton
                      size="sm"
                      label={r.vendor ? `Unlink the ${r.vendor} receipt` : 'Unlink this receipt'}
                      disabled={busy !== null}
                      onClick={() => unlink(r)}
                    >
                      <UnlinkGlyph />
                    </IconButton>
                  </Tooltip>
                ) : undefined
              }
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Tooltip label={r.vendor || ''} truncated>
                    <Link
                      to={`/receipts/${r.pid}`}
                      className="min-w-0 truncate text-sm font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
                    >
                      {r.vendor || 'Receipt'}
                    </Link>
                  </Tooltip>
                  <span className="ml-auto text-sm tabular-nums text-on-surface">{formatCents(r.total_cents)}</span>
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-on-surface-variant">
                  <span className="tabular-nums">
                    {r.purchase_date ? formatEventDate(r.purchase_date, 'MMM D') : 'No date'}
                  </span>
                  <StatusPill status={r.status} />
                  {r.linked_by && <span>Linked by {r.linked_by.name}</span>}
                </div>
              </div>
            </ListRow>
          )}
        />
      ) : (
        <EmptyState variant="line">
          {missing
            ? 'Receipt missing. Link it here once it is uploaded.'
            : req.status === 'approved'
              ? 'Mark it bought to link the receipt.'
              : 'No receipts linked.'}
        </EmptyState>
      )}

      {canLink && (
        <div className="mt-3">
          <AnchoredPopover
            triggerLabel="Link a receipt"
            triggerClassName={LINK_TRIGGER}
            tooltip="Pick one of your uploaded receipts for this request"
            title="Link a receipt"
            width="lg"
            disabled={busy !== null}
            onOpen={() => setPicked([])}
          >
            {(close) => (
              <div className="space-y-3">
                <ReceiptCandidates
                  requisitionId={req.id}
                  excludePids={receipts.map((r) => r.pid)}
                  selected={picked}
                  onChange={setPicked}
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    disabled={busy !== null || picked.length === 0}
                    onClick={async () => {
                      if (await link()) close();
                    }}
                    className={buttonClass('filled', 'sm')}
                  >
                    {busy === 'link'
                      ? 'Linking…'
                      : picked.length > 1
                        ? `Link ${picked.length} receipts`
                        : 'Link receipt'}
                  </button>
                </div>
              </div>
            )}
          </AnchoredPopover>
        </div>
      )}
    </Panel>
  );
};

export default ReceiptsCard;
